import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Grid,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { toast } from 'sonner';

import { deploymentInstanceService } from '../services/DeploymentInstanceService';
import { configService } from '../services/ConfigService';

interface Model {
  id: string;
  model_name: string;
  version: string;
  provider: string;
  description: string;
  type: string;
  size?: string;
  status: string;
  icon: string;
  iconBg: string;
  featured?: boolean;
  capabilities?: string[];
  pricing: {
    input: string;
    output: string;
  };
}

interface ModelDetailViewProps {
  model: Model;
  onClose: () => void;
  onDeploy?: (modelId: string) => void;
}

export function ModelDetailView({ model, onClose, onDeploy }: ModelDetailViewProps) {

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [deployedConfigs, setDeployedConfigs] = useState<any[]>([]);
  
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const getConfig = await configService.getConfig();
      setDeployedConfigs(getConfig);
    } catch (error) {
      console.error('Error fetching deployment instances:', error);
    }
  };
  

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const deployModel = async (id: string) => {
    try {
      setIsDeleting(true);
      const deploymentInstance = await deploymentInstanceService.deploymentInstance(id);      
      console.log('Deployment Instance:', deploymentInstance);
      setDeleteDialogOpen(false);
      setSelectedModel(null);
      toast.success(`"Deployed successfully"`, {
        duration: 4000,
      });
    } catch (error: any) {
      console.error('Error fetching deployment instances:', error.response.data.detail);
      toast.error(`"${error.response.data.detail}"`, {
        duration: 4000,
      });
      setDeleteDialogOpen(false);
    }  finally {
      setIsDeleting(false);
    }
  };

  const deployModelInstance = async (model: any) => {
    try {
      const result = deployedConfigs.filter((config) => {
        return config.model_id === model.id;
      })
      if (result.length) {
        deployModel(result[0].deployment_config_id);
      }
    } catch (error) {
      console.error('Error fetching deployment instances:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'white' }}>
      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Enhanced Header */}
        <Box 
          sx={{ 
            bgcolor: 'white',
            px: 3,
            py: 2,
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton 
                onClick={onClose}
                sx={{ 
                  color: '#64748b',
                  bgcolor: '#f8fafc',
                  '&:hover': { bgcolor: '#e2e8f0' }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, color: '#0f172a' }}>
                  {model.model_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip 
                    label={model.version}
                    size="small"
                    sx={{ 
                      bgcolor: '#f1f5f9',
                      color: '#475569',
                      fontWeight: 600,
                      border: '1px solid #e2e8f0',
                      height: 24
                    }}
                  />
                  <Chip 
                    label={model.provider}
                    size="small"
                    sx={{ 
                      bgcolor: '#f1f5f9',
                      color: '#475569',
                      fontWeight: 600,
                      border: '1px solid #e2e8f0',
                      height: 24
                    }}
                  />
                  <Chip 
                    icon={model.status === 'deployed' ? <CheckCircleIcon /> : undefined}
                    label={model.status}
                    size="small"
                    sx={{ 
                      bgcolor: model.status === 'deployed' ? '#dcfce7' : '#dbeafe',
                      color: model.status === 'deployed' ? '#166534' : '#1e40af',
                      fontWeight: 600,
                      border: `1px solid ${model.status === 'deployed' ? '#86efac' : '#93c5fd'}`,
                      height: 24
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* <Button
              variant="contained"
              size="large"
              // onClick={() => onDeploy?.(model.id)}
              onClick={(e) => {
                  e.stopPropagation();
                  setSelectedModel(model);
                  setDeleteDialogOpen(true)
              }}
              sx={{
                px: 3,
                py: 1,
                fontWeight: 700,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6b3f94 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 16px rgba(102, 126, 234, 0.4)'
                },
                transition: 'all 0.3s'
              }}
            >
              Deploy Model
            </Button> */}
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* Overview Content */}
          {(
            <Box>
              {/* Description Section */}
              <Box sx={{ px: 4, py: 3, bgcolor: 'white' }}>
                <Typography 
                  variant="overline" 
                  sx={{ 
                    color: '#94a3b8', 
                    fontWeight: 700, 
                    letterSpacing: 1.2,
                    display: 'block',
                    mb: 1
                  }}
                >
                  OVERVIEW
                </Typography>
                <Typography variant="body1" sx={{ color: '#334155', lineHeight: 1.8, fontSize: '1rem' }}>
                  {model.description}
                </Typography>
              </Box>

              {/* Divider */}
              <Box sx={{ height: 2, bgcolor: '#f1f5f9' }} />

              {/* Model Specifications Section */}
              <Box sx={{ px: 4, py: 3, bgcolor: '#fafbfc' }}>
                <Typography 
                  variant="overline" 
                  sx={{ 
                    color: '#94a3b8', 
                    fontWeight: 700, 
                    letterSpacing: 1.2,
                    display: 'block',
                    mb: 2
                  }}
                >
                  SPECIFICATIONS
                </Typography>
                <Grid container spacing={3}>
                  {[
                    { 
                      label: 'Model Size', 
                      value: model.size || 'N/A'
                    },
                    { 
                      label: 'Model Type', 
                      value: model.type
                    },
                    { 
                      label: 'Context Window', 
                      value: model.size === 'Large' ? '128K tokens' : model.size === 'Medium' ? '32K tokens' : '8K tokens'
                    },
                    { 
                      label: 'Provider', 
                      value: model.provider
                    }
                  ].map((stat, index) => (
                    <Grid spacing={6} key={index}>
                      <Box sx={{ borderLeft: '3px solid #cbd5e1', pl: 2 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#64748b', 
                            fontWeight: 600, 
                            display: 'block', 
                            mb: 0.5,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: '0.7rem'
                          }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 700, 
                            color: '#0f172a',
                            fontSize: '1.1rem'
                          }}
                        >
                          {stat.value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Divider */}
              <Box sx={{ height: 2, bgcolor: '#f1f5f9' }} />

              {/* Key Capabilities and Pricing Section */}
              <Box sx={{ px: 4, py: 3, bgcolor: 'white' }}>
                <Grid container spacing={5}>
                  {/* Key Capabilities - Left Side */}
                  <Grid spacing={12}>
                    <Box sx={{ borderBottom: '2px solid #e2e8f0', pb: 2, mb: 3 }}>
                      <Typography 
                        variant="overline" 
                        sx={{ 
                          color: '#94a3b8', 
                          fontWeight: 700, 
                          letterSpacing: 1.2
                        }}
                      >
                        KEY CAPABILITIES
                      </Typography>
                    </Box>
                    <Box
                      component="ul"
                      sx={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                      }}
                    >
                      {model?.capabilities?.map((capability: string, index: number) => (
                        <Box
                          component="li"
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            py: 1,
                            borderLeft: '2px solid #e2e8f0',
                            pl: 2,
                            position: 'relative',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: -5,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: '#0f172a'
                            }
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#334155',
                              lineHeight: 1.7,
                              fontSize: '0.95rem'
                            }}
                          >
                            {capability}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Grid>

                  {/* Pricing - Right Side */}
                  <Grid spacing={12}>
                    <Box sx={{ borderBottom: '2px solid #e2e8f0', pb: 2, mb: 3 }}>
                      <Typography 
                        variant="overline" 
                        sx={{ 
                          color: '#94a3b8', 
                          fontWeight: 700, 
                          letterSpacing: 1.2
                        }}
                      >
                        PRICING INFORMATION
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <Box 
                        sx={{ 
                          p: 3, 
                          border: '2px solid #e2e8f0',
                          borderRadius: 1,
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: 4,
                            height: '100%',
                            bgcolor: '#0f172a'
                          }
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600, 
                            textTransform: 'uppercase', 
                            letterSpacing: 1, 
                            color: '#64748b', 
                            display: 'block', 
                            mb: 1,
                            fontSize: '0.7rem'
                          }}
                        >
                          INPUT TOKENS
                        </Typography>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: 800, 
                            mb: 0.5, 
                            color: '#0f172a'
                          }}
                        >
                          {model.pricing.input}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          per 1,000 tokens
                        </Typography>
                      </Box>
                      <Box 
                        sx={{ 
                          p: 3, 
                          border: '2px solid #e2e8f0',
                          borderRadius: 1,
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: 4,
                            height: '100%',
                            bgcolor: '#0f172a'
                          }
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontWeight: 600, 
                            textTransform: 'uppercase', 
                            letterSpacing: 1, 
                            color: '#64748b', 
                            display: 'block', 
                            mb: 1,
                            fontSize: '0.7rem'
                          }}
                        >
                          OUTPUT TOKENS
                        </Typography>
                        <Typography 
                          variant="h4" 
                          sx={{ 
                            fontWeight: 800, 
                            mb: 0.5, 
                            color: '#0f172a'
                          }}
                        >
                          {model.pricing.output}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          per 1,000 tokens
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </Box>
      </Box>


            {/* Delete Confirmation Dialog */}
            <Dialog
              open={deleteDialogOpen}
              onClose={handleDeleteCancel}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  border: '1px solid #e2e8f0'
                }
              }}
            >
              <Box>
                <DialogTitle>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    Create Deployment
                  </Typography>
                </DialogTitle>
              </Box>
              <DialogContent>
                <DialogContentText sx={{ color: '#64748b' }}>
                  Are you sure you want to create a new Instance?
                </DialogContentText>
               
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                <Button
                  onClick={handleDeleteCancel}
                  disabled={isDeleting}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#e2e8f0',
                    color: '#64748b',
                    '&:hover': {
                      borderColor: '#cbd5e1',
                      bgcolor: '#f8fafc'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    deployModelInstance(selectedModel);
                  }}
                  disabled={isDeleting}
                  variant="contained"
                  // startIcon={isDeleting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DeleteIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: '#ef4444',
                    '&:hover': {
                      bgcolor: '#dc2626'
                    },
                    '&:disabled': {
                      bgcolor: '#fca5a5',
                      color: 'white'
                    }
                  }}
                >
                  {isDeleting ? 'Deploying...' : 'Deploy'}
                </Button>
              </DialogActions>
              
            </Dialog>
      
      
    </Box>
  );
}
